import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchRecordDetails } from 'reducers/recordDetailsReducer';
import {
	getDetailsApiPath,
	getRecordTitle,
	isPropertyHidden,
	prettifyPropName,
} from 'utils/recordDetailsUtils';
import { isAdminUser } from 'utils/userUtils';
import { COMBINED } from 'data/dataSourceConstants';
import { dataConfig } from 'data/dataConfig';
import Spinner from 'components/Spinner';
import OutboundLink from 'components/OutboundLink';
import StandardModal from 'modals/StandardModal';
import HousingComplaingProblems from 'formatters/HousingComplaintProblems';
import DotLienParties from 'formatters/DotLienParties';
import DotLienLots from 'formatters/DotLienLots';
import { getOpenDataLink } from 'utils/openDataQueryLinks';

import './RecordDetailsModal.css';

const RecordDetailsModal = ({ options }) => {
	const dispatch = useDispatch();
	const { id, dataSet, dataSource, row } = options;
	const detailsUrlPath = getDetailsApiPath(dataSet);
	const { isLoading, data, user } = useSelector((state) => ({
		isLoading: state.recordDetails.isLoading,
		data: state.recordDetails.data || {},
		user: state.user.userData,
	}));
	const isAdmin = isAdminUser(user);

	const useRowDetails = dataSource === COMBINED || dataConfig[dataSet]?.detailsFromRow;
	let recordDetails = useRowDetails ? row || {} : data;

	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => {
		if (!useRowDetails) {
			dispatch(fetchRecordDetails(id, detailsUrlPath));
		}
	}, [id, detailsUrlPath, dataSource, useRowDetails]);

	// Links to the raw source records (OpenData / BIS) are admin-only, same as the
	// data source link in the datasets table header
	let openDataUrl = isAdmin ? getOpenDataLink(dataSet, recordDetails) : null;
	let bisUrl = isAdmin ? recordDetails?.bisUrl : null;

	const modalTitle = (
		<div className="record-details-title-container">
			<div className="record-details-heading">{`${getRecordTitle(dataSet)} Details`}</div>
			{(openDataUrl || bisUrl) && (
				<div className="record-details-header-links">
					{openDataUrl && (
						<OutboundLink
							url={openDataUrl}
							label="View on OpenData"
						/>
					)}
					{bisUrl && (
						<OutboundLink
							url={bisUrl}
							label="View on BIS"
						/>
					)}
				</div>
			)}
		</div>
	);

	return (
		<StandardModal
			title={modalTitle}
			className="record-details-modal-v3"
			size="lg"
			closeOnOutsideClick
		>
			{isLoading ? (
				<div className="py-5 text-center"><Spinner /></div>
			) : (
				<div className="record-details-content">
					<table className="record-details-table">
						<tbody>
							{Object.keys(recordDetails).map((key, i) => {
								if (isPropertyHidden(key, dataSet, true)) {
									return null;
								}

								let value = recordDetails[key];
								if (value && typeof value === 'object') {
									value = Array.isArray(value)
										? value
												.map((item) =>
													item && typeof item === 'object'
														? [item.name, item.city, item.state]
																.filter(Boolean)
																.join(', ')
														: String(item)
												)
												.filter(Boolean)
												.join('; ')
										: JSON.stringify(value);
								}

								//ecb complaints have a nested structure. 1 "complaint" has many "problems"
								// http://localhost:3000/property/660%20Madison%20Ave%2C%20New%20York%2C%20NY%2010065/hpdHousingComplaintsOpenData
								if (
									dataSet === 'hpdHousingComplaints' &&
									key === 'od_hpd_housing_complaint_problems'
								) {
									value = (
										<HousingComplaingProblems problems={recordDetails[key]} />
									);
								}

								//dot liens nest their party list and block/lot pairs; render
								//them like the housing complaint problems above instead of
								//letting raw objects reach the JSX (which throws)
								if (dataSet === 'dotLiens' && key === 'parties') {
									value = <DotLienParties parties={recordDetails[key]} />;
								}
								if (dataSet === 'dotLiens' && key === 'lots') {
									value = <DotLienLots lots={recordDetails[key]} />;
								}

								return (
									<tr key={key}>
										<td className="key-cell" title={isAdmin ? key : undefined}>
											{prettifyPropName(key, dataSet)}
										</td>
										<td className="value-cell">{value || '-'}</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
		</StandardModal>
	);
};

export default RecordDetailsModal;
